import type { ToolAnswer } from '../mcp-types';
import { ActionField, ActionRow, secretFieldNames } from './action-table';

export type SetOrNot = 'set' | 'not set';

/** All a secret field ever reads as. */
export function setOrNot(value: unknown): SetOrNot {
  if (value === undefined || value === null || value === false || value === '') return 'not set';
  if (Array.isArray(value)) return value.length ? 'set' : 'not set';
  if (typeof value === 'object') return Object.keys(value).length ? 'set' : 'not set';
  return 'set';
}

const SECRET_WORD = '[secret]';

/** Each literal secret the call carried, longest first, so one that contains another goes whole. */
function secretsIn(values: Record<string, unknown>, fields: readonly ActionField[]): string[] {
  const secrets = new Set<string>();
  for (const f of fields) {
    const v = values[f.name];
    if (f.secret && typeof v === 'string' && v) secrets.add(v);
  }
  return [...secrets].sort((a, b) => b.length - a.length);
}

function scrubbed(text: string, secrets: readonly string[]): string {
  return secrets.reduce((out, secret) => out.split(secret).join(SECRET_WORD), text);
}

function clean(value: unknown, secrets: readonly string[], secretKeys: ReadonlySet<string>): unknown {
  if (typeof value === 'string') return scrubbed(value, secrets);
  if (Array.isArray(value)) return value.map((el) => clean(el, secrets, secretKeys));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [
      scrubbed(k, secrets),
      secretKeys.has(k) ? setOrNot(v) : clean(v, secrets, secretKeys),
    ]));
  }
  return value;
}

/**
 * The one way an action's answer leaves the tool. Usage records keep `changed` and `refusal`, so it covers every part
 * of the answer; and it runs on whatever a row's behaviour wrote, so no later behaviour has to remember to.
 */
export function answerWithoutSecrets(answer: ToolAnswer, values: Record<string, unknown>, row: ActionRow, table: readonly ActionRow[]): ToolAnswer {
  return clean(answer, secretsIn(values, row.fields), new Set(secretFieldNames(table))) as ToolAnswer;
}

/** The same cleaning for words that aren't an answer yet, such as what a behaviour threw. */
export function wordsWithoutSecrets(words: string, values: Record<string, unknown>, row: ActionRow): string {
  return scrubbed(words, secretsIn(values, row.fields));
}
